import React, { useEffect, useMemo } from 'react';

import { supabase } from 'hooks/useAuth';
import { useMemberGroupData } from 'hooks/useMemberGroupData';
import { useToast } from 'hooks/useToast';

import { formatDate } from 'libs/utils';

import { DimensionResponses } from 'components/ui/DimensionResponses';

import { colors, Fonts, spacing } from 'styles/theme';
import { Bumper, NestedCard } from 'styles/shared';
import { Link } from 'react-router-dom';

interface RetroResultsProps {
    retroId: string;
}

export const RetroResults = ({ retroId }: RetroResultsProps) => {
    const { retros } = useMemberGroupData();
    const { showErrorToast } = useToast();

    const [signedImageUrls, setSignedImageUrls] = React.useState<string[]>([]);

    const selectedRetro = useMemo(
        () => retros.find((retro) => retro.id === parseInt(retroId, 10)),
        [retroId, retros]
    );
    const hasImages =
        selectedRetro?.image_urls != null &&
        selectedRetro.image_urls.length > 0;

    useEffect(() => {
        const getSignedUrls = async () => {
            const { data, error } = await supabase.storage
                .from('retro-images')
                .createSignedUrls(selectedRetro?.image_urls ?? [], 3600);

            if (error) {
                showErrorToast(error.message);
                return;
            }
            setSignedImageUrls(data.map((d) => d.signedUrl));
        };

        if (hasImages) {
            getSignedUrls();
        }
    }, [hasImages, selectedRetro, showErrorToast]);

    return (
        <>
            <Fonts.Heading5>{`Your Grand Retro`}</Fonts.Heading5>
            <Fonts.Medium $color={colors.mountainAsh}>
                {formatDate(new Date(selectedRetro?.created_at ?? 0))}
            </Fonts.Medium>
            <Bumper />
            {hasImages ? (
                <>
                    <Fonts.Medium $isOpenSansSemiBold>
                        {`Submitted images:`}
                    </Fonts.Medium>
                    <Bumper $minHeight={spacing.xSmall} />
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '16px',
                        }}
                    >
                        {signedImageUrls.map((imageUrl, index) => (
                            <Link key={index} to={imageUrl} rel="noreferrer">
                                <img
                                    src={imageUrl}
                                    alt={`Retro upload ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        border: '2px solid #ddd',
                                        transition: 'border-color 0.2s ease',
                                    }}
                                />
                            </Link>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <Fonts.Medium>{`Here are the totals for each of your dimensions.`}</Fonts.Medium>
                    <Bumper />
                    <DimensionResponses
                        retroDataPayload={selectedRetro as object}
                    />
                    <Bumper />
                    <NestedCard>
                        <Fonts.Medium $isOpenSansSemiBold>
                            {`Your reflections:`}
                        </Fonts.Medium>
                        <Bumper $minHeight={spacing.xxxSmall} />
                        <Fonts.MediumQuote $color={colors.mountainAsh}>
                            {selectedRetro?.reflections ?? ''}
                        </Fonts.MediumQuote>
                        <Bumper />
                        <Fonts.Medium $isOpenSansSemiBold>
                            {`Your reactions:`}
                        </Fonts.Medium>
                        <Bumper $minHeight={spacing.xxxSmall} />
                        <Fonts.MediumQuote $color={colors.mountainAsh}>
                            {selectedRetro?.reactions ?? ''}
                        </Fonts.MediumQuote>
                    </NestedCard>
                </>
            )}
        </>
    );
};
